import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { User, RegisterPayload } from "~/types";
import { api } from "~/lib/api";

interface AuthState {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginMagic: (email: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      login: async (email, password) => {
        const { user } = await api.login(email, password);
        set({ user });
      },
      loginMagic: async (email) => {
        await api.loginMagic(email);
      },
      register: async (payload) => {
        await api.register(payload);
      },
      logout: () => set({ user: null }),
    }),
    {
      name: "fnd.auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ user: s.user }),
    },
  ),
);

/**
 * Derived auth flag. Exposed as a separate selector hook because
 * defining it as a getter inside the create() literal would be
 * flattened by zustand's shallow `set()` merge on the first state
 * update, freezing the boolean at its initial value.
 */
export const useIsAuthed = (): boolean =>
  useAuthStore((s) => s.user !== null);
